import { useCallback, useMemo } from 'react';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { DealsService, type Deal } from '@/services/requests/housien-services/DealsService';
import { lastEventTimestamp } from '@features/deals/utils/deal-events';
import {
  ATTENDANCE_COLUMNS,
  isColumnWorthShowing,
  type AttendanceColumnId,
} from '../constants/attendance-columns';
import {
  stagesForColumn,
  type AttendanceFilters,
} from '../constants/attendance-filters';
import { attendancesKeys } from './attendances-keys';

/** Cards fetched per column, per page. */
export const COLUMN_PAGE_SIZE = 20;

/**
 * Page size used while a text search is active.
 *
 * The search filters the fetched cards in the browser, so paging at 20
 * per column would make it see *less* than the single 200-row page it
 * replaced. Searching therefore asks for a wide page instead, which
 * keeps the old reach rather than quietly narrowing it. A server-side
 * search would remove the compromise; it is not in this etapa's scope.
 */
export const SEARCH_PAGE_SIZE = 200;

export interface Attendance {
  dealId: string;
  clientId: string;
  clientName: string;
  propertyTitle: string;
  stage: string;
  originChannel: string | null;
  /**
   * When the last fact was recorded, for the card's idle line.
   *
   * The board used to drop the deal's events on the way in, which left
   * the card unable to say the one thing that decides whether it needs
   * the broker today. It is a per-card reading on purpose: the board is
   * paged at {@link COLUMN_PAGE_SIZE} per column, so anything that
   * *aggregated* over the loaded cards would present a slice as a total
   * — the very failure {@link Attendances.searchTruncated} exists to warn
   * about.
   *
   * Never undefined in practice: the model seeds every deal with a
   * `created` event. Typed optional anyway, because a card that cannot
   * date itself must say nothing rather than claim "hoje".
   */
  lastEventAt?: string;
}

export interface AttendanceColumnData {
  id: AttendanceColumnId;
  /** The column's true total, from the server — never the cards' length. */
  count: number;
  attendances: Attendance[];
  hasMore: boolean;
  loadMore: () => void;
  isLoadingMore: boolean;
}

export interface Attendances {
  isLoading: boolean;
  isError: boolean;
  columns: AttendanceColumnData[];
  /** The same opportunities, ungrouped — what the table view renders. */
  deals: Deal[];
  /**
   * Whether the text search could not see every attendance.
   *
   * The search filters fetched cards in the browser over a wide page,
   * so a column holding more than that page reaches the browser
   * truncated. Saying so is the whole point: a search that quietly
   * looks at part of the data answers "nothing found" with the same
   * face it uses for "nothing exists".
   */
  searchTruncated: boolean;
  refresh: () => void;
}

/**
 * Maps a deal onto the flat shape the board renders.
 */
function toAttendance(deal: Deal): Attendance {
  return {
    dealId: deal.deal_id,
    clientId: deal.client_id ?? '',
    clientName: deal.client_name ?? deal.client_id ?? '',
    propertyTitle: deal.property_title ?? deal.property_id ?? '',
    stage: deal.stage,
    originChannel: (deal.origin as { channel?: string } | null)?.channel ?? null,
    lastEventAt: lastEventTimestamp(deal.events ?? []),
  };
}

/**
 * Sum the server's per-stage counts for the stages a column covers.
 */
function countFor(
  stageCounts: Record<string, number> | undefined,
  stages: readonly string[],
): number {
  if (!stageCounts) return 0;
  return stages.reduce((total, stage) => total + (stageCounts[stage] ?? 0), 0);
}

/**
 * The page number and page size to fetch for one column.
 */
function columnPaging(
  columnId: AttendanceColumnId,
  pages: Partial<Record<AttendanceColumnId, number>>,
  searching: boolean,
): { page: number; size: number } {
  const page = searching ? 1 : (pages[columnId] ?? 1);
  const size = searching ? SEARCH_PAGE_SIZE : COLUMN_PAGE_SIZE * page;
  return { page, size };
}

/** The server-side filter parameters shared across a column's query. */
interface ColumnQueryFilters {
  originChannel: string;
  brokerId: string;
  selectedStages: readonly string[];
}

/**
 * The react-query descriptor for one board column.
 *
 * @param agencyId - The current agency.
 * @param column - The column definition (id and its covered stages).
 * @param queryFilters - Origin channel, corretor and stage selection.
 * @param paging - The current page/size, from {@link columnPaging}.
 * @returns The query key, fetcher and enabled flag for `useQueries`.
 */
function buildColumnQuery(
  agencyId: string,
  column: (typeof ATTENDANCE_COLUMNS)[number],
  queryFilters: ColumnQueryFilters,
  paging: { page: number; size: number },
) {
  const { originChannel, brokerId, selectedStages } = queryFilters;
  const stages = stagesForColumn(column.stages, selectedStages);
  return {
    queryKey: attendancesKeys.column(
      agencyId, column.id, { originChannel, stages, brokerId }, paging.size,
    ),
    queryFn: () => DealsService.listDeals(
      agencyId, paging.page, paging.size, undefined, undefined, stages, originChannel, brokerId,
    ),
    enabled: Boolean(agencyId) && stages.length > 0,
  };
}

/** One column's query result, as `useQueries` returns it. */
type ColumnResult = { data?: { data?: Deal[]; stage_counts?: Record<string, number> }; isFetching?: boolean };

/**
 * Maps one column definition and its query result onto board column data,
 * or an empty array when the column has no stages or is not worth showing.
 *
 * @param column - The column definition.
 * @param result - The matching query result.
 * @param selectedStages - The active stage selection.
 * @param onLoadMore - Asks the caller to advance a column's page.
 * @returns A single-element array of column data, or empty to hide it.
 */
function toColumnData(
  column: (typeof ATTENDANCE_COLUMNS)[number],
  result: ColumnResult | undefined,
  selectedStages: readonly string[],
  onLoadMore: (columnId: AttendanceColumnId) => void,
): AttendanceColumnData[] {
  const stages = stagesForColumn(column.stages, selectedStages);
  if (stages.length === 0) return [];
  const rows = (result?.data?.data ?? []).map(toAttendance);
  const count = countFor(result?.data?.stage_counts, stages);
  const requested = stages.some((stage) => selectedStages.includes(stage));
  if (!isColumnWorthShowing(column.id, count, requested)) return [];
  return [{
    id: column.id,
    count,
    attendances: rows,
    hasMore: rows.length < count,
    loadMore: () => onLoadMore(column.id),
    isLoadingMore: Boolean(result?.isFetching) && rows.length > 0,
  }];
}

/**
 * The attendance board: one query per column, counts from the server.
 *
 * One row is one opportunity, not one person — a client looking at three
 * properties is three attendances, which is what lets the board say
 * *which* property each conversation is about.
 *
 * Previously this fetched a single page of 200 and grouped it in the
 * browser, reading neither `pages` nor `items`. Past 200 attendances the
 * column totals were simply wrong with nothing saying so, and the
 * origin-channel filter — applied client-side over that same page —
 * searched only the rows that happened to be in it. Both now come from
 * the server: each column asks for its own stages, and the totals come
 * from `stage_counts`, which spans everything matching the other
 * filters.
 *
 * The header's filters are all server-side parameters here. The stage
 * filter is applied by *narrowing* each column's `stages` rather than by
 * sifting fetched cards: a column whose stages the selection excludes
 * entirely asks nothing and drops off the board, so its absence and its
 * count can never disagree.
 *
 * @param agencyId - The current agency.
 * @param filters - Origin channel, stage selection and corretor.
 * @param pages - Current page per column, owned by the caller.
 * @param onLoadMore - Asks the caller to advance a column's page.
 * @param searching - Whether a text search is active; widens the page.
 * @returns The view-model: loading/error flags and the visible columns.
 */
export function useAttendances(
  agencyId: string,
  filters: AttendanceFilters = {},
  pages: Partial<Record<AttendanceColumnId, number>> = {},
  onLoadMore: (columnId: AttendanceColumnId) => void = () => {},
  searching = false,
): Attendances {
  const queryClient = useQueryClient();
  const { originChannel = '', stages: selectedStages = [], brokerId = '' } = filters;
  const queryFilters: ColumnQueryFilters = { originChannel, brokerId, selectedStages };

  const results = useQueries({
    queries: ATTENDANCE_COLUMNS.map((column) => buildColumnQuery(
      agencyId, column, queryFilters, columnPaging(column.id, pages, searching),
    )),
  });

  const columns = useMemo<AttendanceColumnData[]>(
    () => ATTENDANCE_COLUMNS.flatMap(
      (column, index) => toColumnData(column, results[index], selectedStages, onLoadMore),
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [results.map((r) => r.dataUpdatedAt).join(','), onLoadMore, selectedStages.join(',')],
  );

  const deals = useMemo(
    () => results.flatMap((result) => result?.data?.data ?? []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [results.map((r) => r.dataUpdatedAt).join(',')],
  );

  // Only meaningful while searching: outside a search the columns page
  // deliberately, with "carregar mais" saying so on the column itself.
  const searchTruncated = searching
    && columns.some((column) => column.count > SEARCH_PAGE_SIZE);

  const refresh = useCallback(
    () => queryClient.invalidateQueries({
      queryKey: attendancesKeys.all(agencyId),
    }),
    [queryClient, agencyId],
  );

  return {
    isLoading: results.some((result) => result.isLoading),
    isError: results.some((result) => result.isError),
    columns,
    deals,
    searchTruncated,
    refresh,
  };
}
